import React, {useCallback, useEffect, useMemo, useState} from "react";
import {getUserExperience, getUserProjects} from "../../../../service/userService";
import BasicGrid from "../../../grid/BasicGrid";
import {Button, Divider, toaster} from "rsuite";
import SuccessNotification from "../../../notifications/SuccessNotification";
import {
    PROJECT_ADD_ERROR,
    PROJECT_ADD_SUCCESS,
    PROJECT_EDIT_ERROR,
    PROJECT_EDIT_SUCCESS
} from "../../../../service/constants";
import ErrorNotification from "../../../notifications/ErrorNotification";
import {parseISO} from "rsuite/cjs/utils/dateUtils";
import AddProjectModal from "../../../modals/projects/AddProjectModal";
import EditProjectModal from "../../../modals/projects/EditProjectModal";

// there is a lot of information that is not displayed in the grid, but available on edit
const defaultColumns = [
    {
        key: 'position',
        label: 'Position',
        flexGrow: 2,
    },
    {
        key: 'company',
        label: 'Company',
        flexGrow: 2,
    },
    {
        key: 'projectName',
        label: 'Project Name',
        flexGrow: 2,
    },
    {
        key: 'startDate',
        label: 'Date Start',
    },
    {
        key: 'endDate',
        label: 'Date End',
    },
];

const UserEditProjects = (props) => {

    const { selectedUser } = props;

    const [loading, setLoading] = useState(false);
    const [userExperience, setUserExperience] = useState([]);
    const [userProjects, setUserProjects] = useState([]);

    // add modal
    const [addModalOpen, setAddModalOpen] = useState(false);

    // edit modal
    const [selectedProject, setSelectedProject] = useState({});
    const [editModalOpen, setEditModalOpen] = useState(false);

    // we need to associate experience with the projects, this comes in as an id
    const loadUserProjects = useCallback(() => {
        setLoading(true);
        getUserExperience(selectedUser.id).then((experience) => {
            experience.forEach((exp) => {
                exp.startDate = parseISO(exp.startDate);
                exp.endDate = parseISO(exp.endDate);
            });
            experience.sort((a, b) => (a.endDate < b.endDate) ? 1 : -1);
            setUserExperience(experience.map(ele => ({expId: ele.id, label: `${ele.position}-${ele.company}`, value: ele.id})));

            getUserProjects(selectedUser.id).then((projects) => {
                projects.forEach((project) => {
                    project.startDate = parseISO(project.startDate);
                    project.endDate = parseISO(project.endDate);
                    const matchingExp = experience.filter((exp) => exp.id === project.expId)[0];
                    project.company = matchingExp.company;
                    project.position = matchingExp.position;
                });
                projects.sort((a, b) => (a.endDate < b.endDate) ? 1 : -1);
                setUserProjects(projects);
            });
            setLoading(false);
        });
    }, [selectedUser.id]);

    useEffect(() => loadUserProjects(), [loadUserProjects]);

    const handleAddClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={PROJECT_ADD_SUCCESS} />, { placement: 'topCenter' });
            loadUserProjects();
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={PROJECT_ADD_ERROR} />, { placement: 'topCenter' });
        }
        setAddModalOpen(false);
    },[loadUserProjects]);

    const handleEditClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={PROJECT_EDIT_SUCCESS} />, { placement: 'topCenter' });
            loadUserProjects();
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={PROJECT_EDIT_ERROR} />, { placement: 'topCenter' });
        }
        setEditModalOpen(false);
    }, [loadUserProjects]);

    // table
    const [columnKeys] = useState(defaultColumns.map(column => column.key));
    const columns = defaultColumns.filter(column => columnKeys.some(key => key === column.key));

    const setClickedProject = useCallback((rowData) => {
        setEditModalOpen(true);
        setSelectedProject(rowData);
    },[]);

    return useMemo(() => {
        return (
            <div className="main-content">
                <Button appearance="primary" onClick={() => setAddModalOpen(true)}>Add Project</Button>
                <Divider />
                <BasicGrid
                    loading={loading}
                    columns={columns}
                    data={userProjects}
                    handleClick={setClickedProject}
                    exportTitle={`admin-edit-${selectedUser.lastName}-${selectedUser.firstName}-projects`}
                />
                <AddProjectModal
                    key={`add-proj-modal-${selectedUser.id}`}
                    open={addModalOpen}
                    handleClose={handleAddClose}
                    userExperience={userExperience}
                    userId={selectedUser.id}
                />
                <EditProjectModal
                    key={`edit-proj-modal-${selectedUser.id}`}
                    open={editModalOpen}
                    handleClose={handleEditClose}
                    project={selectedProject}
                    userExperience={userExperience}
                    userId={selectedUser.id}
                />
            </div>
        );
    }, [
        addModalOpen,
        columns,
        editModalOpen,
        handleAddClose,
        handleEditClose,
        loading,
        selectedProject,
        setClickedProject,
        userExperience,
        userProjects,
        selectedUser.id,
        selectedUser.firstName,
        selectedUser.lastName,
    ]);
}

export default UserEditProjects;